import { FormControlLabel } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

//COMPONENTS
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import HubContainer from "@/main/components/shared/hub/HubContainer";
import AreaDeadlineAnswerRequote from "@/main/components/shared/areaDeadlineAnswerRequote/areaDeadlineAnswerRequote";
import { SkeletonLoading } from "@/main/components/shared/styles";
import { ReasonFilterRequest, RequoteNationalRequest, RequoteQuoteItemRequest } from "@/main/models/request";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { Controls } from "@/main/services/constants/constantsControl";
import { SendUpdatesSignalRTypeEnum } from "@/main/services/enum/SendUpdatesSignalRTypeEnum";
import { signalRClearReceiveUpdatesByKey } from "@/main/store/signalR";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";

//MODELS/API
import { quoteApi } from "@/main/api";
import { GetItemsToRequoteResponse, QuoteNoSupplierAssignedItemResponse } from "@/main/models/response";
import { getReason, useReasonReducer } from "@/main/store/reason";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { CartState } from "@/main/store/cart";

//REDUX/UTILS
import {
	getDefaultCountryOptionDrop,
	getGuidScreenByRoute,
	getTranslateByKey,
	isEmpty,
	isNullUndefined,
	isNullUndefinedEmpty,
	isShowControlByElementId,
} from "@/main/services/utils";
import { KeyValueDefault } from "@/main/models/common";

// STYLES/COMPONENTS LOCAL
import ModalConfirmRequotePart from "../modalConfirmRequote/modalConfirmRequote";
import AreaPartsSelectRequote from "./areaPartsSelectRequote/areaPartsSelectRequote";
import ModalConfirmRequoteNational from "./modalConfirmRequoteNational/modalConfirmRequoteNational";
import * as S from "./styles";
import { getPartTypesByDirectAssignCriteria } from "@/main/store/partType";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { store } from "@/main/store";
import { convertStringForBoolean } from "@/main/services/utilsString";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { RefreshQuoteByGetItemsType } from "../types";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { getRefreshForceUrl } from "@/main/services/utilsQueryParams";
import Button from "@/main/components/shared/button/button";

export type TypeSectionPart = "abovePrive" | "unanswered";
export interface IQuoteNoSupplierAssignedItemResponse extends QuoteNoSupplierAssignedItemResponse {
	sectionPart: TypeSectionPart;
	partType: KeyValueDefault | null;
	isSelected: boolean;
}
export interface IQuoteNoSupplierAssignedItemType {
	type: TypeSectionPart;
	items: IQuoteNoSupplierAssignedItemResponse[];
}

export interface IRequoterequest {
	deadlineAnswer: number;
	parts: IQuoteNoSupplierAssignedItemType[];
	reason: KeyValueDefault | null;
	reasonDescription: "";
}

interface IModalRequotePartsProps {
	isOpenModal: boolean;
	guidQuote: string;
	numbersParts?: number;
	dataCart: CartState;
	showPartTypeControl: boolean;
	loadCart: (guidQuote: string) => void;
	loadInfoOrder: (guidQuote: string) => void;
	refreshQuoteByGetItems: RefreshQuoteByGetItemsType;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	setIsQuoteBlocked: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
}

function ModalRequoteParts({
	isOpenModal,
	guidQuote,
	dataCart,
	showPartTypeControl,
	setIsOpenModal,
	closeModal,
	refreshQuoteByGetItems,
	setIsQuoteBlocked,
}: IModalRequotePartsProps) {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultSearchOrderDaysInterval);
	const { countriesUser: countryReducer } = useCountryReducer();
	const { reason: reasonReducer } = useReasonReducer();
	const location = useLocation();

	const cartNoSupplierAssigned = dataCart.cartItems.dataNoSupplierAssigned;
	const cartDataUnansweredItems = dataCart.cartItems.dataUnansweredItems;

	const [dataParts, setDataParts] = useState<GetItemsToRequoteResponse | null>(null);
	const [isLoadingParts, setIsLoadingParts] = useState(false);
	const [openModalConfirmRequote, setOpenModalConfirmRequote] = useState<boolean>(false);
	const [openModalConfirmRequoteNational, setOpenModalConfirmRequoteNational] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [isLoadingSignalR, setIsLoadingSignalR] = useState<boolean>(false);
	const [isErrorSignalR, setIsErrorSignalR] = useState<boolean>(false);
	const [isRequotePartCanceled, setIsRequotePartCanceled] = useState<boolean>(false);
	const [claimNumber, setClaimNumber] = useState<string>("");

	const showDeadlineAnswerInput = isShowControlByElementId(
		location.pathname == RoutesConstants.orderSearchOrder
			? Controls.IdRequoteAnswerProcurementTime
			: Controls.IdRequoteAnswerTime,
	);

	const showRequoteNational = isShowControlByElementId(
		location.pathname == RoutesConstants.orderSearchOrder
			? Controls.IdRequoteNationalProcurement
			: Controls.IdCreateOrderRequoteNational,
	);

	let initialDeadline = dataParts?.paramSupplierDeadlineAnswer || 0;
	const minDeadline = dataParts?.paramMinCustomDeadlineAnswer || 30;
	const maxDeadline = dataParts?.ParamMaxCustomDeadlineAnswer || 300;
	if (initialDeadline < minDeadline) initialDeadline = minDeadline;
	if (initialDeadline > maxDeadline) initialDeadline = maxDeadline;

	const INITIAL_FORM_STATE: IRequoterequest = {
		deadlineAnswer: initialDeadline,
		parts: [],
		reason: null as KeyValueDefault | null,
		reasonDescription: "",
	};

	const methods = useForm<IRequoterequest>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});
	const {
		control,
		formState: { isValid },
		getValues,
		setValue,
		watch,
	} = methods;
	const { fields, replace } = useFieldArray({ control, name: "parts" });
	const watcherFields = watch("parts");
	const controlledFields = fields.map((field, index) => {
		return {
			...field,
			...watcherFields[index],
		};
	});
	const watcherReasonType = watch("reason");
	const isReasonOther = convertStringForBoolean(watcherReasonType?.default);
	const watcherReasonDescription = watch("reasonDescription");

	const allCheckboxesSelected = useMemo(() => {
		if (isNullUndefined(controlledFields)) return false;
		return controlledFields.every((item) => item.items.every((i) => i.isSelected));
	}, [JSON.stringify(controlledFields)]);

	function getDeadlineAnswer(): number | undefined {
		if (!showDeadlineAnswerInput) return;
		return getValues("deadlineAnswer");
	}
	//#region Core functions
	function validateForm() {
		const data = getValues();
		const hasOneSelected = data.parts.some((item) => item.items.some((i) => i.isSelected));

		if (isNullUndefined(data.reason)) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.reason").toLowerCase(),
				),
			);
			return false;
		}

		if (isReasonOther && isNullUndefinedEmpty(data.reasonDescription)) {
			toast.error(getTranslateByKey("information.descriptionRequired", true));
			return false;
		}

		if (!hasOneSelected) {
			const messageError = getTranslateByKey("information.selectOne", true).replace(
				"{0}",
				getTranslateByKey("information.part").toLowerCase(),
			);
			toast.error(messageError);
			return false;
		}
		if (showDeadlineAnswerInput) {
			if (!isValid) {
				const messageError = getTranslateByKey("quote.requoteDeadlineAnswerInvalid", true)
					.replace("{0}", minDeadline.toString())
					.replace("{1}", maxDeadline.toString())
					.replace("{2}", (getDeadlineAnswer() || "").toString());

				toast.error(messageError);
				return false;
			}
		}
		return true;
	}

	async function fetchPartsRequote() {
		if (isLoadingParts) return;

		setIsLoadingParts(true);
		quoteApi
			.getItemsToRequote({ guidQuote })
			.then((response) => {
				const {
					data: { result },
				} = response;

				setDataParts(result);
				setClaimNumber(result.claimNumber);

				const allInactive = result.unansweredQuoteItems?.every((item) => item.active === false);

				if (allInactive) {
					setIsRequotePartCanceled(true);
				}

				function mapDataPartsToFields(
					type: TypeSectionPart,
					dataParts: QuoteNoSupplierAssignedItemResponse[],
				): IQuoteNoSupplierAssignedItemType {
					return {
						type,
						items: dataParts.map((item) => ({
							...item,
							sectionPart: type,
							partType: isNullUndefined(item.idPartType)
								? null
								: {
										key: item.idPartType,
										value: "",
										default: String(item.idPartType),
									},
							isSelected: true,
						})),
					};
				}

				const mapperDataPartsAbovePrice = mapDataPartsToFields("abovePrive", result.abovePriceCapQuoteItems);
				const mapperDataPartsUnaswered = mapDataPartsToFields("unanswered", result.unansweredQuoteItems);

				const resultFields = [mapperDataPartsAbovePrice, mapperDataPartsUnaswered];
				replace(resultFields);
			})
			.finally(() => {
				setIsLoadingParts(false);
			});
	}

	async function onSubmit() {
		if (!validateForm()) return;
		setOpenModalConfirmRequote(true);
	}

	async function onSubmitIOpenModalNational() {
		if (!validateForm()) return;
		setOpenModalConfirmRequoteNational(true);
	}

	function getAllOptionsSelected(data: IQuoteNoSupplierAssignedItemType[]): IQuoteNoSupplierAssignedItemResponse[] {
		const allOptionsSelected: IQuoteNoSupplierAssignedItemResponse[] = [];

		if (isNullUndefined(data)) return allOptionsSelected;
		data.forEach((part) => {
			if (isNullUndefined(part.items)) return;
			part.items.forEach((item) => {
				if (item.isSelected) {
					allOptionsSelected.push(item);
				}
			});
		});

		return allOptionsSelected;
	}

	async function startRequoteSignalR() {
		setIsErrorSignalR(false);
		dispatch(signalRClearReceiveUpdatesByKey(SendUpdatesSignalRTypeEnum.RequoteNational));
		setIsQuoteBlocked(true);
		setIsLoadingSave(false);
		setIsLoadingSignalR(true);
	}

	async function onSubmitRequoteNational() {
		if (!validateForm()) return;
		const data = getValues();
		setIsLoadingSave(true);
		setIsErrorSignalR(false);
		dispatch(signalRClearReceiveUpdatesByKey(SendUpdatesSignalRTypeEnum.RequoteNational));

		const allOptionsSelected = getAllOptionsSelected(data.parts);
		const formattedRequoteItems: RequoteQuoteItemRequest[] = allOptionsSelected.map((item) => ({
			guidQuoteItem: item.guidQuoteItem,
			idPartType: isEmpty(item.partType?.default) ? null : Number(item.partType?.default),
			replacePartType: showPartTypeControl,
		}));

		const request: RequoteNationalRequest = {
			guidQuote: guidQuote,
			quoteItems: formattedRequoteItems,
			userName: String(store.getState().user.currentUser?.userName),
			deadlineAnswerRequote: getDeadlineAnswer(),
			guidReason: String(data.reason?.key),
			reasonDescription: data.reasonDescription,
			guidScreen: getGuidScreenByRoute(
				location.pathname === RoutesConstants.orderSearchOrder
					? RoutesConstants.orderSearchOrder
					: RoutesConstants.orderCreateOrder,
			),
		};

		quoteApi
			.requoteNationalItems(request)
			.then(() => {
				setIsQuoteBlocked(true);
			})
			.finally(() => {
				setIsLoadingSave(false);
				setIsLoadingSignalR(true);
			});
	}

	function onFinishedRequote() {
		if (isLoadingSignalR) {
			const filters = getRefreshForceUrl({
				quoteFilterVehicle: claimNumber,
			});
			const routeRedirect = `${RoutesConstants.orderSearchOrder}${filters}`;
			navigate(routeRedirect);
			setIsLoadingSignalR(false);
			setIsOpenModal(false);
			setIsQuoteBlocked(false);
		}
	}
	function onErrorRequote() {
		setIsLoadingSignalR(false);
		setIsErrorSignalR(true);
		setIsQuoteBlocked(false);
	}

	useEffect(() => {
		if (!isOpenModal && isErrorSignalR) {
			dispatch(signalRClearReceiveUpdatesByKey(SendUpdatesSignalRTypeEnum.RequoteNational));
			setIsErrorSignalR(false);
			setIsLoadingSignalR(false);
		}
	}, [dispatch, isErrorSignalR, isOpenModal]);

	function generateRequestPart(): RequoteQuoteItemRequest[] {
		const allOptionsSelected = getAllOptionsSelected(controlledFields);
		return allOptionsSelected.map((i) => ({
			guidQuoteItem: i.guidQuoteItem,
			idPartType: isEmpty(i.partType?.default) ? null : Number(i.partType?.default),
			replacePartType: showPartTypeControl,
		}));
	}
	//#endregion

	//#region Handler Functions
	function handleToggleCheckboxAll() {
		if (isNullUndefined(dataParts)) return;
		const isAllCheckboxesSelected = controlledFields.every((item) => item.items.every((i) => i.isSelected));
		controlledFields.forEach((item, index) => {
			item.items.forEach((i, indexItem) => {
				setValue(`parts.${index}.items.${indexItem}.isSelected`, !isAllCheckboxesSelected);
			});
		});
	}

	function handleToggleCheckbox(_idPart: string | string[]) {}

	//#endregion
	useEffect(() => {
		if (isOpenModal) {
			fetchPartsRequote();
			if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
			dispatch(getPartTypesByDirectAssignCriteria({ guidQuote }));
		}
	}, [isOpenModal]);

	useEffect(() => {
		if (!isNullUndefined(dataParts)) {
			let initialDeadline = dataParts?.paramSupplierDeadlineAnswer || 0;
			const minDeadline = dataParts?.paramMinCustomDeadlineAnswer || 30;
			const maxDeadline = dataParts?.ParamMaxCustomDeadlineAnswer || 300;
			if (initialDeadline < minDeadline) initialDeadline = minDeadline;
			if (initialDeadline > maxDeadline) initialDeadline = maxDeadline;

			setValue("deadlineAnswer", initialDeadline);
		}
	}, [dataParts]);

	useEffect(() => {
		const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
		if (countryUser === null) return;

		const requestReason: ReasonFilterRequest = {
			guidCountry: String(countryUser.key),
			idReasonType: ReasonTypeEnum.RequoteQuote,
		};
		dispatch(getReason(requestReason));
	}, [JSON.stringify(countryReducer.data)]);

	//#region Mini Components
	const AreaSelectAll = () => {
		if (!isLoadingParts && isNullUndefined(dataParts)) return null;

		return (
			<S.AreaSelectAll>
				{isLoadingParts ? (
					<>
						<SkeletonLoading $height="10px" $forceWidth="100px" />
						<SkeletonLoading $height="20px" $forceWidth="20px" $margin="0 auto" />
					</>
				) : (
					<FormControlLabel
						label={getTranslateByKey("information.selectAll")}
						labelPlacement="start"
						control={
							<KogCheckbox
								id={`id-createOrder-checkbox-selectAll`}
								checked={allCheckboxesSelected}
								value={allCheckboxesSelected}
								onChange={() => handleToggleCheckboxAll()}
							/>
						}
					/>
				)}
			</S.AreaSelectAll>
		);
	};
	//#endregion

	return (
		<FormProvider {...methods}>
			<S.Container>
				{isLoadingSignalR || isErrorSignalR ? (
					<HubContainer
						type={SendUpdatesSignalRTypeEnum.RequoteNational}
						customProps={{
							onFinishedRequote: onFinishedRequote,
							onErrorRequote: onErrorRequote,
						}}
					/>
				) : (
					<div>
						<S.RowHeader>
							<h4>{getTranslateByKey("information.requoteParts")}</h4>
							<AreaSelectAll />
						</S.RowHeader>

						<S.RowBody>
							<AreaPartsSelectRequote
								type="unanswered"
								dataParts={dataParts?.unansweredQuoteItems}
								indexSupplier={controlledFields.findIndex((i) => i.type === "unanswered")}
								handleToggleCheckbox={handleToggleCheckbox}
								isLoadingParts={isLoadingParts}
								numbersParts={(cartDataUnansweredItems?.parts ?? []).length}
								showPartTypeControl={showPartTypeControl}
							/>
							<AreaPartsSelectRequote
								type="abovePrive"
								dataParts={dataParts?.abovePriceCapQuoteItems}
								indexSupplier={controlledFields.findIndex((i) => i.type === "abovePrive")}
								handleToggleCheckbox={handleToggleCheckbox}
								isLoadingParts={isLoadingParts}
								numbersParts={(cartNoSupplierAssigned?.parts ?? []).length}
								showPartTypeControl={showPartTypeControl}
							/>
							{showDeadlineAnswerInput && !isLoadingParts && (
								<AreaDeadlineAnswerRequote min={minDeadline} max={maxDeadline} control={control} />
							)}
						</S.RowBody>
						<S.AreaReason>
							<KogAutoCompleteControlled
								id="id-requote-parts-quote-select-reason"
								nameCustom="reason"
								isDefaultValueUnique
								label={getTranslateByKey("information.reason")}
								control={control}
								rulesCustom={{ required: true }}
								setValueCustom={setValue}
								options={reasonReducer.data}
								isLoading={reasonReducer.isLoading}
								onChangeCustom={(option) => {
									if (option === null) return;

									if (convertStringForBoolean((option as KeyValueDefault)?.default)) {
										setValue("reasonDescription", "");
									}
								}}
							/>
							{isReasonOther && (
								<KogInputTextControlledNew
									required={isReasonOther}
									id="id-requote-quote-input-description"
									label={getTranslateByKey("information.description")}
									name="reasonDescription"
									control={control}
								/>
							)}
						</S.AreaReason>
					</div>
				)}
				<S.AreaButtons>
					<SecondaryButton size="small" id="id-createOrder-btn-back" type="button" onClick={closeModal}>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					{!isLoadingSignalR && (
						<Button size="small" id="id-createOrder-btn-save" type="button" style={{ margin: 0 }} onClick={onSubmit}>
							{getTranslateByKey("information.requote")}
						</Button>
					)}
					{!isLoadingSignalR && showRequoteNational && (
						<Button
							size="small"
							id="id-createOrder-btn-requote-national"
							type="button"
							isLoading={isLoadingSave}
							style={{ margin: 0 }}
							onClick={onSubmitIOpenModalNational}
						>
							{getTranslateByKey("information.requoteNational")}
						</Button>
					)}
				</S.AreaButtons>

				<ModalConfirmRequotePart
					type="unasweredBulkRequote"
					dataCart={dataCart}
					isRequotePartCanceled={isRequotePartCanceled}
					refreshQuoteByGetItems={refreshQuoteByGetItems}
					partSelected={generateRequestPart()}
					openModalConfirm={openModalConfirmRequote}
					getDeadlineAnswer={getDeadlineAnswer}
					setOpenModalConfirm={setOpenModalConfirmRequote}
					callbackAfterRemovePart={() => {}}
					reason={watcherReasonType}
					reasonDescription={watcherReasonDescription}
					callbackAfterRequotePart={() => {
						setOpenModalConfirmRequoteNational(false);
						startRequoteSignalR();
					}}
				/>
				<ModalConfirmRequoteNational
					dataCart={dataCart}
					isRequotePartCanceled={isRequotePartCanceled}
					openModalConfirm={openModalConfirmRequoteNational}
					guidsQuoteItem={getAllOptionsSelected(controlledFields).map((i) => i.guidQuoteItem)}
					onClickActionConfirm={() => {
						onSubmitRequoteNational();
					}}
					setOpenModalConfirm={setOpenModalConfirmRequoteNational}
					callbackAfterRequotePart={() => {
						setOpenModalConfirmRequoteNational(false);
					}}
				/>
			</S.Container>
		</FormProvider>
	);
}

function WrapperModalRequoteParts(props: IModalRequotePartsProps) {
	return (
		<KogModalSideComponent shouldRenderOpen open={props.isOpenModal} closeModal={props.closeModal}>
			<ModalRequoteParts {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalRequoteParts;
